<template>
  <a-modal
    :title="title"
    :width="1200"
    :visible="visible"
    :confirmLoading="confirmLoading"
    @ok="handleSubmit"
    @cancel="handleCancel"
  >
    <a-spin :spinning="confirmLoading">
      <a-form-model ref="form" :model="form" :rules="rules" :label-col="{ span: 3 }" :wrapper-col="{ span: 20 }">
        <a-form-model-item label="选择供应商" prop="supplierId">
          <a-space>
            <a-select
              v-model="form.supplierId"
              placeholder="请选择供应商"
              style="width: 300px"
              @change="handleSupplierChange"
            >
              <a-select-option v-for="item in supplierOptions" :key="item.id" :value="item.id">
                {{ item.supplierName }}
              </a-select-option>
            </a-select>
            <a-button type="link" @click="handleGetLastPurchase">
              <a-icon type="history" />获取上次采购记录
            </a-button>
          </a-space>
        </a-form-model-item>

        <a-form-model-item label="选择商品">
          <a-select
            v-model="selectedGoodsIds"
            mode="multiple"
            placeholder="请选择采购商品（支持搜索）"
            show-search
            :filter-option="false"
            :default-active-first-option="false"
            :not-found-content="notFoundContent"
            :dropdownMatchSelectWidth="false"
            dropdownClassName="purchase-goods-dropdown"
            @search="handleGoodsSearch"
            @change="handleGoodsChange"
            @dropdownVisibleChange="handleDropdownVisibleChange"
            style="width: 100%"
          >
            <template v-if="goodsSearchLoading" slot="notFoundContent">
              <a-spin size="small" />
            </template>
            <template v-else slot="notFoundContent">
              <span>暂无数据</span>
            </template>
            <a-select-option v-for="item in goodsOptions" :key="item.id" :value="item.id">
              <span class="goods-option-text">{{ item.goodsName }}<template v-if="item.skuName"> - {{ item.skuName }}</template></span>
            </a-select-option>
          </a-select>
        </a-form-model-item>

        <a-form-model-item label="备注" prop="remark">
          <a-textarea
            v-model="form.remark"
            placeholder="请输入备注信息（选填）"
            :rows="2"
            :maxLength="200"
            style="width: 600px"
          />
        </a-form-model-item>
      </a-form-model>

      <!-- 商品采购数量价格设置区域 -->
      <div>
        <a-divider>商品采购数量价格设置</a-divider>

        <!-- 统一设置区域 -->
        <a-form style="margin-bottom: 16px; background: #f5f5f5; padding: 12px; border-radius: 4px;">
          <a-form-item label="统一设置">
            <a-input-number
              v-model="uniformSettings.initialStock"
              :min="0"
              :precision="0"
              :max="999999"
              placeholder="库存"
              style="width: 100px"
            />
            <a-input-number
              v-model="uniformSettings.alarmStock"
              :min="0"
              :precision="0"
              :max="999999"
              placeholder="预警"
              style="width: 100px; margin-left: 8px"
            />
            <a-input-number
              v-model="uniformSettings.purchasePrice"
              :min="0"
              :precision="2"
              :max="999999.99"
              placeholder="采购价"
              style="width: 120px; margin-left: 8px"
            />
            <a-input-number
              v-model="uniformSettings.merchantPrice"
              :min="0"
              :precision="2"
              :max="999999.99"
              placeholder="拿货价"
              style="width: 120px; margin-left: 8px"
            />
            <a-button type="primary" @click="handleUniformSet" style="margin-left: 8px">
              <a-icon type="check" />统一设置
            </a-button>
            <a-button @click="handleResetAll" style="margin-left: 8px">
              <a-icon type="undo" />重置所有
            </a-button>
          </a-form-item>
        </a-form>

        <!-- 商品表格 -->
        <a-table
          :columns="goodsColumns"
          :data-source="selectedGoods"
          :pagination="false"
          size="small"
          rowKey="id"
          :scroll="{ y: 400 }"
        >
          <span slot="goodsName" slot-scope="text, record">
            <img v-if="record.picture" :src="record.picture" style="width: 40px; height: 40px; margin-right: 8px; vertical-align: middle;" />
            {{ record.goodsName }}
            <a-tag v-if="record.skuName" color="orange" style="margin-left: 8px">{{ record.skuName }}</a-tag>
          </span>
          <span slot="initialStock" slot-scope="text, record">
            <a-input-number
              v-model="record.initialStock"
              :min="0"
              :precision="0"
              :max="999999"
              style="width: 100px"
              placeholder="采购数量"
            />
          </span>
          <span slot="alarmStock" slot-scope="text, record">
            <a-input-number
              v-model="record.alarmStock"
              :min="0"
              :precision="0"
              :max="999999"
              style="width: 100px"
              placeholder="预警"
            />
          </span>
          <span slot="purchasePrice" slot-scope="text, record">
            <a-input-number
              v-model="record.purchasePrice"
              :min="0"
              :precision="2"
              :max="999999.99"
              style="width: 100px"
              placeholder="采购价"
            />
          </span>
          <span slot="merchantPrice" slot-scope="text, record">
            <a-input-number
              v-model="record.merchantPrice"
              :min="0"
              :precision="2"
              :max="999999.99"
              style="width: 100px"
              placeholder="拿货价"
            />
          </span>
          <span slot="action" slot-scope="text, record">
            <a-button type="link" size="small" @click="removeGoods(record)">
              <a-icon type="delete" />移除
            </a-button>
          </span>
        </a-table>
      </div>
    </a-spin>
  </a-modal>
</template>

<script>
import { batchInitInventory, getPlatformGoods, getLastPurchaseRecord } from '@/api/retail/inventory'
import { getAllSuppliers, getSupplierGoods } from '@/api/retail/supplier'
import { getGoods } from '@/api/goods/goods'
import { debounce } from 'lodash'

export default {
  name: 'InitForm',
  data() {
    return {
      supplierOptions: [],

      visible: false,
      confirmLoading: false,
      title: '采购入库',
      selectedGoodsIds: [],
      selectedGoods: [],
      goodsOptions: [],
      goodsSearchLoading: false,
      notFoundContent: '暂无数据',
      form: {
        orgId: undefined,
        orgType: undefined,
        supplierId: undefined,
        remark: ''
      },
      rules: {
        supplierId: [{ required: true, message: '请选择供应商', trigger: 'change' }],
      },
      goodsColumns: [
        {
          title: '商品信息',
          dataIndex: 'goodsName',
          key: 'goodsName',
          scopedSlots: { customRender: 'goodsName' },
          width: 300,
          fixed: 'left'
        },
        {
          title: '采购数量',
          dataIndex: 'initialStock',
          key: 'initialStock',
          scopedSlots: { customRender: 'initialStock' },
          width: 110,
          align: 'center'
        },
        {
          title: '预警',
          dataIndex: 'alarmStock',
          key: 'alarmStock',
          scopedSlots: { customRender: 'alarmStock' },
          width: 110,
          align: 'center'
        },
        {
          title: '采购价(元)',
          dataIndex: 'purchasePrice',
          key: 'purchasePrice',
          scopedSlots: { customRender: 'purchasePrice' },
          width: 110,
          align: 'center'
        },
        {
          title: '拿货价(元)',
          dataIndex: 'merchantPrice',
          key: 'merchantPrice',
          scopedSlots: { customRender: 'merchantPrice' },
          width: 110,
          align: 'center'
        },
        {
          title: '操作',
          key: 'action',
          scopedSlots: { customRender: 'action' },
          width: 80,
          align: 'center',
          fixed: 'right'
        }
      ],
      uniformSettings: {
        initialStock: undefined,
        alarmStock: undefined,
        purchasePrice: undefined,
        merchantPrice: undefined
      }
    }
  },
  created() {
    this.handleGoodsSearch = debounce(this.searchGoods, 300)
  },
  methods: {
    handleDropdownVisibleChange(visible) {
      if (visible && this.goodsOptions.length === 0 && !this.goodsSearchLoading) {
        this.loadGoods()
      }
    },
    handleSupplierChange(supplierId) {
      this.selectedGoodsIds = []
      this.selectedGoods = []
      this.goodsOptions = []
      this.loadGoods()
    },
    loadGoods() {
      if (!this.form.supplierId) {
        this.goodsSearchLoading = true
        getPlatformGoods({
          pageNum: 1,
          pageSize: 50
        }).then(response => {
          this.goodsOptions = response.data.records || []
          this.goodsSearchLoading = false
        }).catch(() => {
          this.goodsSearchLoading = false
        })
      } else {
        this.goodsSearchLoading = true
        getSupplierGoods({ supplierId: this.form.supplierId }).then(response => {
          if (response.data && Array.isArray(response.data)) {
            this.goodsOptions = response.data
          } else {
            this.goodsOptions = []
          }
          this.goodsSearchLoading = false
        }).catch(() => {
          this.goodsSearchLoading = false
        })
      }
    },
    handleAdd() {
      this.form.orgId = this.$store.getters.orgId
      this.form.orgType = this.$store.getters.orgType
      this.form.supplierId = undefined
      this.form.remark = ''
      this.selectedGoodsIds = []
      this.selectedGoods = []
      this.goodsOptions = []
      this.supplierOptions = []
      this.uniformSettings = {
        initialStock: undefined,
        alarmStock: undefined,
        purchasePrice: undefined,
        merchantPrice: undefined
      }
      this.loadGoods()
      this.getSupplierList()
      this.visible = true
    },
    getSupplierList() {
      getAllSuppliers().then(response => {
        this.supplierOptions = response.data || []
      }).catch(() => {
        this.supplierOptions = []
      })
    },
    searchGoods(value) {
      console.log('InitForm searchGoods:', value)
      if (!value || value.trim() === '') {
        this.loadGoods()
        return
      }
      this.goodsSearchLoading = true

      if (!this.form.supplierId) {
        getPlatformGoods({
          pageNum: 1,
          pageSize: 50,
          goodsName: value
        }).then(response => {
          this.goodsOptions = response.data.records || []
          this.goodsSearchLoading = false
        }).catch(() => {
          this.goodsSearchLoading = false
        })
      } else {
        getSupplierGoods({ supplierId: this.form.supplierId }).then(response => {
          if (response.data && Array.isArray(response.data)) {
            const keyword = value.trim().toLowerCase()
            this.goodsOptions = response.data.filter(item => {
              const goodsName = (item.goodsName || '').toLowerCase()
              const skuName = (item.skuName || '').toLowerCase()
              return goodsName.includes(keyword) || skuName.includes(keyword)
            })
          } else {
            this.goodsOptions = []
          }
          this.goodsSearchLoading = false
        }).catch(() => {
          this.goodsSearchLoading = false
        })
      }
    },
    getGoodsDetail(goodsId) {
      return getGoods({ id: goodsId }).then(response => {
        return response.data
      })
    },
    async handleGoodsChange(value) {
      const existingIds = this.selectedGoods.map(item => item.goodsId)
      const newIds = value.filter(id => !existingIds.includes(id))

      if (newIds.length > 0) {
        for (const id of newIds) {
          try {
            const goodsDetail = await this.getGoodsDetail(id)
            if (goodsDetail) {
              if (goodsDetail.skuList && goodsDetail.skuList.length > 0) {
                goodsDetail.skuList.forEach(sku => {
                  const exists = this.selectedGoods.find(item => item.id === sku.id)
                  if (!exists) {
                    this.selectedGoods.push({
                      id: sku.id,
                      goodsId: goodsDetail.id,
                      goodsName: goodsDetail.goodsName,
                      skuName: sku.skuName,
                      picture: sku.picture || goodsDetail.picture,
                      initialStock: 0,
                      alarmStock: 0,
                      purchasePrice: sku.price || 0,
                      merchantPrice: sku.price || 0
                    })
                  }
                })
              } else {
                const exists = this.selectedGoods.find(item => item.id === goodsDetail.id)
                if (!exists) {
                  const defaultSkuId = goodsDetail.id
                  this.selectedGoods.push({
                    id: defaultSkuId,
                    goodsId: goodsDetail.id,
                    goodsName: goodsDetail.goodsName,
                    skuName: '',
                    picture: goodsDetail.picture,
                    initialStock: 0,
                    alarmStock: 0,
                    purchasePrice: goodsDetail.price || 0,
                    merchantPrice: goodsDetail.price || 0
                  })
                }
              }
            }
          } catch (error) {
            console.error('获取商品详情失败:', error)
          }
        }
      }

      const remainingGoodsIds = value
      const filteredGoods = this.selectedGoods.filter(item => {
        if (item.skuName) {
          return remainingGoodsIds.includes(item.goodsId)
        } else {
          return remainingGoodsIds.includes(item.goodsId)
        }
      })

      this.selectedGoods = filteredGoods
    },
    removeGoods(record) {
      if (record.skuName) {
        const sameGoodsSkus = this.selectedGoods.filter(item => item.goodsId === record.goodsId)
        if (sameGoodsSkus.length === 1) {
          const index = this.selectedGoodsIds.indexOf(record.goodsId)
          if (index > -1) {
            this.selectedGoodsIds.splice(index, 1)
          }
        }
      } else {
        const index = this.selectedGoodsIds.indexOf(record.goodsId)
        if (index > -1) {
          this.selectedGoodsIds.splice(index, 1)
        }
      }

      const index = this.selectedGoods.findIndex(item => item.id === record.id)
      if (index > -1) {
        this.selectedGoods.splice(index, 1)
      }
    },
    handleSubmit() {
      this.$refs.form.validate(valid => {
        if (!valid) {
          return false
        }

        if (this.selectedGoods.length === 0) {
          this.$message.warning('请选择商品')
          return
        }

      const invalidGoods = this.selectedGoods.find(item => {
        return item.initialStock === undefined || item.initialStock === null ||
               item.alarmStock === undefined || item.alarmStock === null ||
               item.purchasePrice === undefined || item.purchasePrice === null ||
               item.merchantPrice === undefined || item.merchantPrice === null
      })

      if (invalidGoods) {
        this.$message.warning('请完善所有商品的库存和价格信息')
        return
      }

      this.confirmLoading = true

      const inventoryList = this.selectedGoods.map(item => ({
        goodsId: item.goodsId,
        skuId: item.id,
        stock: item.initialStock,
        alarmStock: item.alarmStock,
        purchasePrice: item.purchasePrice || 0,
        merchantPrice: item.merchantPrice || 0,
        supplierId: this.form.supplierId,
        remark: this.form.remark
      }))

      let supplierName = null
      const selectedSupplier = this.supplierOptions.find(item => item.id === this.form.supplierId)
      if (selectedSupplier) {
        supplierName = selectedSupplier.supplierName
      }

      batchInitInventory(inventoryList).then(() => {
        this.$message.success('采购入库申请已提交,等待审核')
        this.visible = false
        this.$emit('ok')
      }).catch(error => {
        this.$message.error('采购入库申请失败：' + (error.message || '未知错误'))
      }).finally(() => {
        this.confirmLoading = false
      })
      })
    },
    handleCancel() {
      this.visible = false
      this.selectedGoodsIds = []
      this.selectedGoods = []
      this.goodsOptions = []
      this.form.supplierId = undefined
      this.form.remark = ''
      this.uniformSettings = {
        initialStock: undefined,
        alarmStock: undefined,
        purchasePrice: undefined,
        merchantPrice: undefined
      }
    },
    handleUniformSet() {
      if (!this.uniformSettings.initialStock && !this.uniformSettings.alarmStock &&
          !this.uniformSettings.purchasePrice && !this.uniformSettings.merchantPrice) {
        this.$message.warning('请先设置要统一的值')
        return
      }

      this.selectedGoods.forEach(item => {
        if (this.uniformSettings.initialStock !== undefined && this.uniformSettings.initialStock !== null) {
          item.initialStock = this.uniformSettings.initialStock
        }
        if (this.uniformSettings.alarmStock !== undefined && this.uniformSettings.alarmStock !== null) {
          item.alarmStock = this.uniformSettings.alarmStock
        }
        if (this.uniformSettings.purchasePrice !== undefined && this.uniformSettings.purchasePrice !== null) {
          item.purchasePrice = this.uniformSettings.purchasePrice
        }
        if (this.uniformSettings.merchantPrice !== undefined && this.uniformSettings.merchantPrice !== null) {
          item.merchantPrice = this.uniformSettings.merchantPrice
        }
      })

      this.$message.success('统一设置成功')
    },
    handleResetAll() {
      this.$confirm({
        title: '确认重置',
        content: '确定要重置所有商品的采购数量和价格设置为0吗？',
        onOk: () => {
          this.selectedGoods.forEach(item => {
            item.initialStock = 0
            item.alarmStock = 0
            item.purchasePrice = 0
            item.merchantPrice = 0
          })
          this.uniformSettings = {
            initialStock: 0,
            alarmStock: 0,
            purchasePrice: 0,
            merchantPrice: 0
          }
          this.$message.success('重置成功')
        }
      })
    },
    handleGetLastPurchase() {
      getLastPurchaseRecord().then(response => {
        const lastItems = response.data || []
        if (lastItems.length === 0) {
          this.$message.info('暂无历史采购记录')
          return
        }

        this.selectedGoods = []
        this.selectedGoodsIds = []

        lastItems.forEach(item => {
          this.selectedGoods.push({
            id: item.skuId,
            goodsId: item.goodsId,
            goodsName: item.goodsName,
            skuName: item.skuName,
            picture: item.picture,
            initialStock: item.stock || 0,
            alarmStock: item.alarmStock || 0,
            purchasePrice: item.purchasePrice || 0,
            merchantPrice: item.merchantPrice || 0
          })
        })

        const goodsIds = [...new Set(lastItems.map(item => item.goodsId))]
        this.selectedGoodsIds = goodsIds

        this.$message.success('已加载上次采购记录')
      }).catch(() => {
        this.$message.error('获取上次采购记录失败')
      })
    }
  }
}
</script>

<style scoped>
:deep(.ant-table) {
  font-size: 13px;
}
/* 固定列与滚动区域行对齐 */
::deep(.ant-table-fixed-left .ant-table-row),
::deep(.ant-table-fixed-right .ant-table-row) {
  display: flex;
  align-items: center;
}
</style>

<!-- 下拉面板渲染在 body, 需用非 scoped 全局样式修正行错位 -->
<style>
.purchase-goods-dropdown {
  max-height: 400px;
}
.purchase-goods-dropdown .ant-select-dropdown-menu-item {
  white-space: normal;
  line-height: 1.5;
  height: auto;
  min-height: 32px;
  padding: 6px 12px;
  word-break: break-all;
}
.purchase-goods-dropdown .goods-option-text {
  display: inline-block;
  width: 100%;
}
/* 固定列与滚动区域行对齐 */
::deep(.ant-table-fixed-left .ant-table-row),
::deep(.ant-table-fixed-right .ant-table-row) {
  display: flex;
  align-items: center;
}
</style>
